(function () {
  class ShedPriceCalculator {
    constructor(container) {
      this.container = container;
      this.form = container.querySelector('.js-insul-calc-form');
      this.message = container.querySelector('.js-insul-calc-cart-message');
      this.addToCartButton = container.querySelector('.js-insul-calc-add-to-cart');

      this.currencyCode = container.dataset.currencyCode || 'AUD';
      this.rollCoverage = this.toNumber(container.dataset.rollCoverage, 30);
      this.rollWidth = this.toNumber(container.dataset.rollWidth, 1.5);
      this.tapeRollLength = this.toNumber(container.dataset.tapeRollLength, 50);
      this.seamWasteFactor = this.toNumber(container.dataset.seamWasteFactor, 1.1);

      this.productMeta = {
        insulation: {
          selected: container.querySelector('#insul-calc-select-insulation'),
          quantityEl: container.querySelector('#insul-calc-table-quantity-insulation'),
          priceEl: container.querySelector('#insul-calc-table-price-insulation'),
          variantId: this.toInteger(container.dataset.insulationVariantId),
          unitPrice: this.toNumber(container.dataset.insulationUnitPrice, 0)
        },
        tape: {
          selected: container.querySelector('#insul-calc-select-tape'),
          quantityEl: container.querySelector('#insul-calc-table-quantity-tape'),
          priceEl: container.querySelector('#insul-calc-table-price-tape'),
          variantId: this.toInteger(container.dataset.tapeVariantId),
          unitPrice: this.toNumber(container.dataset.tapeUnitPrice, 0)
        }
      };

      this.valueEls = {
        roofArea: container.querySelector('#insul-calc-roof-area'),
        wallArea: container.querySelector('#insul-calc-wall-area'),
        totalArea: container.querySelector('#insul-calc-total-area'),
        tapeNeeded: container.querySelector('#insul-calc-tape-needed'),
        tapeDetail: container.querySelector('#insul-calc-tape-detail'),
        rollsNeeded: container.querySelector('#insul-calc-rolls-needed'),
        rollsDetail: container.querySelector('#insul-calc-rolls-detail'),
        totalCost: container.querySelector('#insul-calc-total-cost')
      };

      this.bindEvents();
      this.recalculate();
    }

    bindEvents() {
      const recalcHandler = this.recalculate.bind(this);
      this.form.querySelectorAll('input').forEach((input) => {
        input.addEventListener('input', recalcHandler);
        input.addEventListener('change', recalcHandler);
      });

      this.productMeta.insulation.selected.addEventListener('change', recalcHandler);
      this.productMeta.tape.selected.addEventListener('change', recalcHandler);
      this.addToCartButton.addEventListener('click', this.addSelectedProducts.bind(this));
    }

    toNumber(value, fallback) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    toInteger(value) {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : null;
    }

    clamp(value, min, max) {
      if (!Number.isFinite(value)) {
        return min;
      }
      return Math.min(Math.max(value, min), max);
    }

    formatArea(value) {
      return `${value.toFixed(2)} m2`;
    }

    formatMoney(value) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currencyCode
      }).format(value);
    }

    getCoverageMode() {
      const checked = this.form.querySelector('input[name="insul-calc-coverage"]:checked');
      return checked ? checked.value : 'both';
    }

    updateAnimatedValue(el, text) {
      if (!el || el.textContent === text) {
        return;
      }
      el.textContent = text;
      el.classList.remove('insul-calc-updated');
      requestAnimationFrame(() => {
        el.classList.add('insul-calc-updated');
      });
    }

    calculateTapeLength(length, width, wallHeight, roofPitchRad, includeRoof, includeWalls) {
      const safeCos = Math.max(Math.cos(roofPitchRad), 0.1);
      const slopeWidth = (width / 2) / safeCos;

      const roofSeamsPerPanel = Math.max(Math.ceil(slopeWidth / this.rollWidth) - 1, 0);
      const roofTapeMeters = roofSeamsPerPanel * length * 2;

      const sideWallSeamsPerWall = Math.max(Math.ceil(length / this.rollWidth) - 1, 0);
      const endWallSeamsPerWall = Math.max(Math.ceil(width / this.rollWidth) - 1, 0);
      const wallTapeMeters = (sideWallSeamsPerWall * wallHeight * 2) + (endWallSeamsPerWall * wallHeight * 2);

      let totalMeters = 0;
      if (includeRoof) {
        totalMeters += roofTapeMeters;
      }
      if (includeWalls) {
        totalMeters += wallTapeMeters;
      }

      return totalMeters * this.seamWasteFactor;
    }

    recalculate() {
      const length = this.clamp(this.toNumber(this.form.querySelector('#insul-calc-length').value, 0), 0, 100);
      const width = this.clamp(this.toNumber(this.form.querySelector('#insul-calc-width').value, 0), 0, 100);
      const wallHeight = this.clamp(this.toNumber(this.form.querySelector('#insul-calc-wall-height').value, 0), 0, 10);
      const roofPitch = this.clamp(this.toNumber(this.form.querySelector('#insul-calc-roof-pitch').value, 0), 0, 45);
      const roofPitchRad = (roofPitch * Math.PI) / 180;

      const safeCos = Math.max(Math.cos(roofPitchRad), 0.1);
      const roofArea = (length * width) / safeCos;
      const wallArea = 2 * (length + width) * wallHeight;

      const mode = this.getCoverageMode();
      const includeRoof = mode === 'both' || mode === 'roof';
      const includeWalls = mode === 'both' || mode === 'walls';

      const totalArea = (includeRoof ? roofArea : 0) + (includeWalls ? wallArea : 0);
      const rollsNeeded = totalArea > 0 ? Math.ceil(totalArea / this.rollCoverage) : 0;

      const tapeMeters = this.calculateTapeLength(length, width, wallHeight, roofPitchRad, includeRoof, includeWalls);
      const tapeNeeded = tapeMeters > 0 ? Math.ceil(tapeMeters / this.tapeRollLength) : 0;

      this.updateAnimatedValue(this.valueEls.roofArea, this.formatArea(roofArea));
      this.updateAnimatedValue(this.valueEls.wallArea, this.formatArea(wallArea));
      this.updateAnimatedValue(this.valueEls.totalArea, this.formatArea(totalArea));
      this.updateAnimatedValue(this.valueEls.rollsNeeded, `${rollsNeeded}`);
      this.updateAnimatedValue(this.valueEls.rollsDetail, `Based on ${this.rollCoverage}m2 coverage per roll`);
      this.updateAnimatedValue(this.valueEls.tapeNeeded, `${tapeNeeded} rolls`);
      this.updateAnimatedValue(this.valueEls.tapeDetail, `${tapeMeters.toFixed(1)}m seam tape estimate (${this.tapeRollLength}m/roll)`);

      this.renderCostRows(rollsNeeded, tapeNeeded);
      this.hideMessage();
    }

    renderCostRows(rollsNeeded, tapeNeeded) {
      const insulationQty = rollsNeeded;
      const tapeQty = tapeNeeded;

      this.productMeta.insulation.quantityEl.textContent = String(insulationQty);
      this.productMeta.tape.quantityEl.textContent = String(tapeQty);

      const insulationTotal = insulationQty * this.productMeta.insulation.unitPrice;
      const tapeTotal = tapeQty * this.productMeta.tape.unitPrice;

      this.productMeta.insulation.priceEl.textContent = this.formatMoney(insulationTotal);
      this.productMeta.tape.priceEl.textContent = this.formatMoney(tapeTotal);

      const selectedInsulationTotal = this.productMeta.insulation.selected.checked ? insulationTotal : 0;
      const selectedTapeTotal = this.productMeta.tape.selected.checked ? tapeTotal : 0;
      const total = selectedInsulationTotal + selectedTapeTotal;

      this.valueEls.totalCost.textContent = this.formatMoney(total);

      this.currentQuantities = {
        insulation: insulationQty,
        tape: tapeQty
      };
    }

    hideMessage() {
      this.message.className = 'insul-calc-cart-message js-insul-calc-cart-message';
      this.message.textContent = '';
    }

    showMessage(type, text) {
      this.message.className = `insul-calc-cart-message js-insul-calc-cart-message ${type}`;
      this.message.textContent = text;
    }

    getAddUrl() {
      if (window.theme && theme.routes && theme.routes.cart_add_url) {
        return theme.routes.cart_add_url.endsWith('.js') ? theme.routes.cart_add_url : `${theme.routes.cart_add_url}.js`;
      }
      return '/cart/add.js';
    }

    async addSelectedProducts(event) {
      event.preventDefault();

      const items = [];
      if (this.productMeta.insulation.selected.checked && this.currentQuantities.insulation > 0 && this.productMeta.insulation.variantId) {
        items.push({
          id: this.productMeta.insulation.variantId,
          quantity: this.currentQuantities.insulation
        });
      }
      if (this.productMeta.tape.selected.checked && this.currentQuantities.tape > 0 && this.productMeta.tape.variantId) {
        items.push({
          id: this.productMeta.tape.variantId,
          quantity: this.currentQuantities.tape
        });
      }

      if (items.length === 0) {
        this.showMessage('error', 'Select at least one product with quantity greater than zero.');
        return;
      }

      this.addToCartButton.classList.add('loading');
      this.addToCartButton.setAttribute('disabled', 'disabled');

      try {
        const response = await fetch(this.getAddUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items: items })
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          const message = data.description || data.message || 'Could not add items to cart.';
          throw new Error(message);
        }

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        const cartDrawer = document.getElementById('Cart-Drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }

        this.showMessage('success', 'Selected products were added to your cart.');
      } catch (error) {
        this.showMessage('error', error.message || 'Could not add items to cart.');
      } finally {
        this.addToCartButton.classList.remove('loading');
        this.addToCartButton.removeAttribute('disabled');
      }
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.js-insulation-price-calculator').forEach((container) => {
      new ShedPriceCalculator(container);
    });
  });
})();
